package com.lightningkite.kiteui.views.l2

import com.lightningkite.kiteui.*
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.navigation.PageNavigator
import com.lightningkite.kiteui.navigation.pageNavigator
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.reactive.AppState
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.reactive.context.*
import com.lightningkite.reactive.core.*
import com.lightningkite.reactive.extensions.*
import com.lightningkite.reactive.lensing.*
import com.lightningkite.readable.*

data class UserInfo(
    val name: String,
    val profileImage: ImageVector? = null,
    val defaultIcon: Icon,
)

interface AppNav {
    var appName: String
    var appIcon: Icon
    var appLogo: ImageSource
    var navItems: List<NavElement>
    var actions: List<NavElement>
    var exists: Boolean

    class ByProperty : AppNav {
        val appNameProperty = Signal("My App")
        override var appName: String by appNameProperty
        val appIconProperty = Signal<Icon>(Icon.home)
        override var appIcon: Icon by appIconProperty
        val appLogoProperty = Signal<ImageSource>(Icon.home.toImageSource(Color.white))
        override var appLogo: ImageSource by appLogoProperty
        val navItemsProperty = Signal(listOf<NavElement>())
        override var navItems: List<NavElement> by navItemsProperty
        val actionsProperty = Signal<List<NavElement>>(listOf())
        override var actions: List<NavElement> by actionsProperty
        val existsProperty = Signal(true)
        override var exists: Boolean by existsProperty
    }
}


val ViewWriter.appNavFactory by rContextAddon<Signal<ViewWriter.(AppNav.() -> Unit) -> ViewModifiable>>(
    Signal(
        ViewWriter::appNavBottomTabs
    )
)

fun ViewWriter.appNav(main: PageNavigator, dialog: PageNavigator? = null, setup: AppNav.() -> Unit): ViewModifiable {
    return appBase(main, dialog) {
        swapView {
            debugName = "swapView for appNavFactory"
            swapping(
                current = { appNavFactory() },
                views = { it(this, setup) }
            )
        }
    }
}

fun ViewWriter.appNavHamburger(setup: AppNav.() -> Unit): ViewModifiable {
    val appNav = AppNav.ByProperty()
    val showMenu = Signal(false)
    return OuterSemantic.onNext - col {
        debugName = "outer nav"
        bar - row {
            applySafeInsets(bottom = false)
            debugName = "top bar"
            showOnPrint = false
            setup(appNav)
            toggleButton {
                checked bind showMenu
                icon(Icon.menu, "Open navigation menu")
            }
            if (Platform.current != Platform.Web) button {
                icon(Icon.arrowBack, "Go Back")
                ::visible { pageNavigator.canGoBack() }
                onClick { pageNavigator.goBack() }
            }
            HeaderSemantic.onNext - centered - expanding - text {
                ::content.invoke { pageNavigator.currentPage()?.title?.let { it() } ?: "" }
                wraps = false
                ellipsis = true
            }
            navGroupActions(appNav.actionsProperty)
            ::shown { appNav.existsProperty() }
        }
        expanding - frame {
            applySafeInsets(top = false)
            debugName = "menu and navigator container"
            navigatorView(pageNavigator)
            atStart - shownWhen(false) { showMenu() && appNav.existsProperty() } - nav - scrolling - navGroupColumn(appNav.navItemsProperty, { showMenu set false }) {
                gap = 0.px
            }
        }
    }
}


fun ViewWriter.appNavTop(setup: AppNav.() -> Unit): ViewModifiable {
    val appNav = AppNav.ByProperty()
    // Nav 2 top, horizontal
    return OuterSemantic.onNext - col {
        bar - row {
            applySafeInsets(bottom = false)
            showOnPrint = false
            setup(appNav)
            if (Platform.current != Platform.Web) button {
                icon(Icon.arrowBack, "Go Back")
                ::visible { pageNavigator.canGoBack() }
                onClick { pageNavigator.goBack() }
            }
            HeaderSemantic.onNext - centered - text {
                ::content.invoke { pageNavigator.currentPage()?.title?.let { it() } ?: "" }
                wraps = false
                ellipsis = true
            }
            space()
            expanding - centered - navGroupTop(appNav.navItemsProperty)
            space()
            centered - navGroupActions(appNav.actionsProperty)
            ::shown { appNav.existsProperty() }
        }
        beforeNextElementSetup {
            applySafeInsets(top = false)
        } - expanding - navigatorView(pageNavigator)
    }
}

fun ViewWriter.appNavBottomTabs(setup: AppNav.() -> Unit): ViewModifiable {
    val appNav = AppNav.ByProperty()
    return OuterSemantic.onNext - col {
        debugName = "outer nav"
// Nav 3 top and bottom (top)
        if (Platform.probablyAppleUser) {
            compact - bar - frame {
                applySafeInsets(bottom = false)
                debugName = "apple app bar"
                showOnPrint = false
                setup(appNav)
                atStart - InteractiveSemantic.onNext - button {
                    row {
                        gap = 0.px
                        centered - icon(Icon.chevronLeft, "Go Back")
                        centered - text {
                            ::content {
                                pageNavigator.stack().let { it.getOrNull(it.size - 2) }?.title?.let { it().let{ if(it.length > 15) it.take(15) + "\u2026" else it } } ?: ""
                            }
                        }
                    }
                    ::visible { pageNavigator.canGoBack() }
                    onClick { pageNavigator.goBack() }
                }
                centered - HeaderSemantic.onNext - centered - expanding - text {
                    ::content.invoke { pageNavigator.currentPage()?.title?.let { it() } ?: "" }
                    wraps = false
                    ellipsis = true
                }
                atEnd - navGroupActions(appNav.actionsProperty)
                ::shown { appNav.existsProperty() }
            }
        } else {
            bar - row {
                applySafeInsets(bottom = false)
                debugName = "normal app bar"
                showOnPrint = false
                setup(appNav)
                if (Platform.current != Platform.Web) button {
                    icon(Icon.arrowBack, "Go Back")
                    ::visible { pageNavigator.canGoBack() }
                    onClick { pageNavigator.goBack() }
                }
                HeaderSemantic.onNext - centered - expanding - text {
                    ::content.invoke { pageNavigator.currentPage()?.title?.let { it() } ?: "" }
                    wraps = false
                    ellipsis = true
                }
                navGroupActions(appNav.actionsProperty)
                ::shown { appNav.existsProperty() }
            }
        }
        beforeNextElementSetup {
            applySafeInsets(top = false, bottom = false)
        }
        expanding - navigatorView(pageNavigator)
        //Nav 3 - top and bottom (bottom/tabs)
        navGroupTabs(appNav.navItemsProperty) {
            applySafeInsets(top = false)
            debugName = "navGroupTabs"
            showOnPrint = false
            ::shown { appNav.existsProperty() && !AppState.softInputOpen() }
        }
    }
}

fun ViewWriter.appNavTopAndLeft(setup: AppNav.() -> Unit): ViewModifiable {
    val appNav = AppNav.ByProperty()
    return OuterSemantic.onNext - col {
// Nav 4 left and top - add dropdown for user info
        bar - row {
            applySafeInsets(bottom = false)
            showOnPrint = false
            setup(appNav)
            if (Platform.current != Platform.Web) button {
                icon(Icon.arrowBack, "Go Back")
                ::visible { pageNavigator.canGoBack() }
                onClick { pageNavigator.goBack() }
            }
            HeaderSemantic.onNext - centered - text {
                ::content.invoke { pageNavigator.currentPage()?.title?.let { it() } ?: "" }
                wraps = false
                ellipsis = true
            }
            expanding - space {}
            navGroupActions(appNav.actionsProperty)

            ::shown { appNav.existsProperty() }
        }
        expanding - OuterSemantic.onNext - row {
            beforeNextElementSetup {
                applySafeInsets(right = false)
            }
            scrolling - navGroupColumn(appNav.navItemsProperty) {
                ::shown { appNav.navItemsProperty().size > 1 && appNav.existsProperty() }
                showOnPrint = false
            }
            beforeNextElementSetup {
                applySafeInsets(top = false)
            }
            expanding - navigatorView(pageNavigator)
        }
    }
}
